import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Crown, ArrowLeft, Clock, Building2, CheckCircle, Loader2 } from "lucide-react";
import DealForgeLogo from "../components/DealForgeLogo";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  lender:     "Lender / Capital Provider",
  realtor:    "Realtor / Agent",
  contractor: "Contractor / GC",
  title:      "Title / Settlement",
  investor:   "Investor / Operator",
};

// ── Invite-specific registration form ─────────────────────────────────
function InviteSignupForm({ role, dealId, prefilledEmail, onSuccess }) {
  const [firstName, setFirstName] = useState("");
  const [email,     setEmail]     = useState(prefilledEmail || "");
  const [market,    setMarket]    = useState("");
  const [loading,   setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName || !email) return toast.error("Name and email are required");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/waitlist/public-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName,
          email,
          role: role || "investor",
          market: market || "General",
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Signup failed");
      onSuccess(firstName);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="invite-signup-form">
      <div>
        <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">First Name *</label>
        <input
          type="text"
          value={firstName}
          onChange={e => setFirstName(e.target.value)}
          placeholder="Your first name"
          className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40"
          data-testid="invite-first-name"
        />
      </div>
      <div>
        <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Email *</label>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40"
          data-testid="invite-email"
        />
      </div>
      <div>
        <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Primary Market</label>
        <input
          type="text"
          value={market}
          onChange={e => setMarket(e.target.value)}
          placeholder="e.g. Austin TX, Nashville TN"
          className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40"
          data-testid="invite-market"
        />
      </div>
      {role && (
        <div className="flex items-center gap-2 px-3 py-2 bg-[#D4AF37]/[0.06] border border-[#D4AF37]/20 rounded-xl">
          <CheckCircle className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
          <span className="text-[11px] text-[#D4AF37] font-semibold">Joining as: {ROLE_LABELS[role] || role}</span>
        </div>
      )}
      <button
        type="submit"
        disabled={loading}
        data-testid="invite-submit-btn"
        className="w-full flex items-center justify-center gap-2 py-3 bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black font-bold rounded-xl transition-all disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />}
        Request Early Access
      </button>
    </form>
  );
}

// ── Main RegisterPage ──────────────────────────────────────────────────
export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const inviteDealId    = searchParams.get("invite");
  const prefilledRole   = searchParams.get("role") || "";
  const prefilledEmail  = searchParams.get("email") || "";

  const isInvite = Boolean(inviteDealId);
  const [submitted, setSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState("");

  // Auto-redirect only when NOT an invite
  useEffect(() => {
    if (isInvite) return;
    const timer = setTimeout(() => navigate("/"), 10000);
    return () => clearTimeout(timer);
  }, [isInvite, navigate]);

  // ── Invite success screen ────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-6">
          <Link to="/" className="inline-flex justify-center mb-4">
            <DealForgeLogo size="2xl" />
          </Link>
          <div className="bg-[#0D0D0D] border border-[#00FF88]/20 rounded-2xl p-8 space-y-4">
            <CheckCircle className="w-12 h-12 text-[#00FF88] mx-auto" />
            <h2 className="text-xl font-bold text-white">You're on the list, {submittedName}!</h2>
            <p className="text-white/50 text-sm">
              We've received your request. The team will review and send you access instructions within 24 hours.
            </p>
            <Link to="/login">
              <button className="w-full py-3 bg-[#D4AF37] text-black font-bold rounded-xl hover:bg-[#D4AF37]/90 transition-all mt-2">
                Already have access? Sign In
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Invite-aware UI ──────────────────────────────────────────────────
  if (isInvite) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center">
            <Link to="/" className="inline-flex justify-center mb-6">
              <DealForgeLogo size="2xl" />
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full mb-4">
              <Building2 className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-[11px] text-[#D4AF37] font-bold uppercase tracking-wider">Transaction Invite</span>
            </div>
            <h1 className="text-2xl font-bold text-white">You've been invited to collaborate</h1>
            <p className="text-white/40 text-sm mt-2">
              Request early access to join this deal on DealForge.
            </p>
          </div>

          <div className="bg-[#0D0D0D] border border-white/[0.08] rounded-2xl p-6">
            <InviteSignupForm
              role={prefilledRole}
              dealId={inviteDealId}
              prefilledEmail={prefilledEmail}
              onSuccess={(name) => { setSubmittedName(name); setSubmitted(true); }}
            />
          </div>

          <p className="text-center text-[11px] text-white/20">
            Already have access?{" "}
            <Link to="/login" className="text-[#D4AF37] hover:underline">Sign in →</Link>
          </p>
        </div>
      </div>
    );
  }

  // ── Default: Beta-only screen ────────────────────────────────────────
  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <Link to="/" className="inline-flex justify-center mb-8">
          <DealForgeLogo size="2xl" />
        </Link>

        <div className="bg-purple-500/10 border border-purple-500/30 p-8 rounded-2xl mb-8">
          <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Crown className="w-8 h-8 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Founder Beta Only</h1>
          <p className="text-white/70 mb-6">
            Account creation is currently limited to approved Founder Beta members.
            Join the waitlist to be notified when we open to the public.
          </p>
          <div className="flex items-center justify-center gap-2 text-white/50 text-sm mb-6">
            <Clock className="w-4 h-4" />
            <span>Redirecting to waitlist in 10 seconds...</span>
          </div>
          <div className="space-y-3">
            <Link to="/"><button className="w-full py-3 bg-gradient-to-br from-[#D4AF37] to-[#AA8C2C] text-black font-semibold rounded-sm hover:opacity-90 transition-opacity">Join the Waitlist</button></Link>
            <Link to="/login"><button className="w-full py-3 bg-[#0A0A0A]/80 backdrop-blur-sm hover:bg-white/[0.06] text-white border border-white/[0.1] font-medium rounded-sm transition-all duration-300 flex items-center justify-center gap-2"><ArrowLeft className="w-4 h-4" />Already have access? Sign in</button></Link>
          </div>
        </div>

        <p className="text-white/40 text-sm">
          Active capital deployer?{" "}
          <Link to="/" className="text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] hover:text-purple-300">Apply for Founder Beta</Link>
        </p>
      </div>
    </div>
  );
}
